//==================================================================================================
// Kitten Cat LLC ("COMPANY") CONFIDENTIAL                                                         =
// Unpublished Copyright (c) 2021 Kitten Cat LLC. All rights reserved.                             =
//                                                                                                 =
// NOTICE: All information contained herein is, and remains the property of COMPANY. The           =
// intellectual and technical concepts contained herein are proprietary to COMPANY and may be      =
// covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret      =
// or copyright law. Dissemination of this information or reproduction of this material is         =
// strictly forbidden unless prior written permission is obtained from COMPANY. Access to the      =
// source code contained herein is hereby forbidden to anyone except current COMPANY               =
// employees, managers, or contractors who have executed Confidentiality and Non-disclosure        =
// agreements explicitly covering such access.                                                     =
//                                                                                                 =
// The copyright notice above does not evidence any actual or intended publication or              =
// disclosure of this source code, which includes information that is confidential and/or          =
// proprietary, and is a trade secret, of COMPANY. ANY REPRODUCTION, MODIFICATION,                 =
// DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE CODE       =
// WITHOUT THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF      =
// APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE       =
// AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR        =
// DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE, IN      =
// WHOLE OR IN PART.                                                                               =
//==================================================================================================

import _ from 'lodash';

// @ts-ignore
import Split from 'split.js';

import * as monaco from 'monaco-editor';
import { contentLoaded, loaded } from 'document-promises';
import ScrollSpy from './scrollspy';

import { loadEditorCommon, EditorContext, EditorCommonOptions } from './editor-common';
import './tutorial.css';
import './footer.css';

function getBootstrapBreakpoint() {
    const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const breakpoints = [
        { size: 'xs', value: 576 },
        { size: 'sm', value: 768 },
        { size: 'md', value: 992 },
        { size: 'lg', value: 1200 },
        { size: 'xl', value: 1400 },
        { size: 'xxl', value: Infinity },
    ];

    const index = _.sortedIndexBy(breakpoints, { value: w }, 'value');
    return breakpoints[index].size;
}

class ResizableGrid {
    columns: Split.Instance;
    rows: Split.Instance;

    constructor(monacoEditor: monaco.editor.IStandaloneCodeEditor) {
        this.columns = Split(['#col1', '#col2'], {
            elementStyle(dimension, size, gutterSize) {
                return {
                    'flex-basis': `calc(${size}% - ${gutterSize}px)`,
                };
            },
            gutterStyle(dimension, gutterSize) {
                return {
                    'flex-basis': `${gutterSize}px`,
                };
            },
            sizes: [30, 30],
            gutterSize: 10,
            cursor: 'col-resize',
            onDragStart() {
                monacoEditor.layout({ width: 100, height: 100 });
            },
            onDragEnd() {
                monacoEditor.layout();
            },
        });
        this.rows = Split(['#row1', '#row2'], {
            direction: 'vertical',
            sizes: [30, 20],
            cursor: 'row-resize',
            onDragStart() {
                monacoEditor.layout({ width: 100, height: 100 });
            },
            onDragEnd() {
                monacoEditor.layout();
            },
        });
    }

    destroy() {
        this.columns.destroy();
        this.rows.destroy();
    }
}

function createTableOfContentsEntries() {
    const root = document.getElementById('toc-ul');
    const headers = [...document.getElementById('col1').getElementsByTagName('h3')];
    headers.forEach((header) => {
        header.classList.add('scrollspy');
        const li = document.createElement('li');
        li.className = 'toc-entry';
        const a = document.createElement('a');
        a.href = `#${header.getAttribute('id')}`;
        a.innerText = header.innerText;
        li.appendChild(a);
        root.appendChild(li);
    });

    const options = {
        sectionClass: '.scrollspy',
        menuActiveTarget: '.toc-entry',
        offset: 100,
        scrollContainer: '#col1',
    };
    // eslint-disable-next-line no-new
    new ScrollSpy(document.getElementById('toc-ul'), options);
}

function createFillOutExampleButtons(monacoEditor: monaco.editor.IStandaloneCodeEditor) {
    const elements = [...document.getElementById('col1').getElementsByTagName('pre')];
    elements.forEach((element) => {
        const button = document.createElement('button');
        button.textContent = 'Fill out example';
        const codeContent: string = element.textContent.trim();
        button.onclick = () => {
            monacoEditor.setValue(codeContent);
        };
        element.parentNode.insertBefore(button, element.nextSibling);
    });
}

(() => {
    let editorContext: EditorContext;

    const contentLoadedPromise: Promise<void> = contentLoaded.then(async () => {
        createTableOfContentsEntries();
        const options: EditorCommonOptions = new EditorCommonOptions({
            updateUrlFragment: false,
            tutorialMode: true,
            monacoEditorAutomaticLayout: false,
        });
        editorContext = await loadEditorCommon(options);
        editorContext.monacoEditor.onDidChangeModelContent(() => {
            editorContext.monacoEditor.layout();
        });
        createFillOutExampleButtons(editorContext.monacoEditor);
    });

    Promise.all([loaded, contentLoadedPromise]).then(() => {
        let splitObject: ResizableGrid = new ResizableGrid(editorContext.monacoEditor);

        const smallBreakpoints: Set<String> = new Set(['sm', 'xs']);
        window.onresize = () => {
            const currentBreakpoint: string = getBootstrapBreakpoint();
            if (smallBreakpoints.has(currentBreakpoint) && splitObject !== null) {
                splitObject.destroy();
                splitObject = null;
            } else if (!smallBreakpoints.has(currentBreakpoint) && splitObject === null) {
                splitObject = new ResizableGrid(editorContext.monacoEditor);
            }
            editorContext.monacoEditor.layout();
        };

        editorContext.monacoEditor.layout();
        window.dispatchEvent(new Event('resize'));
    });
})();
